import React from 'react';
import { FileText, Search } from 'lucide-react';
import ResourceCard from '../ResourceCard';
import styles from './ResourceList.module.css';

/**
 * ResourceList component to handle grid layout and loading states
 * @param {Object} props - Component props
 * @param {Array} props.resources - Array of resource objects
 * @param {boolean} props.loading - Loading state
 * @param {string} props.searchTerm - Current search term for empty state
 * @param {Array} props.selectedCategories - Selected categories for empty state
 * @param {Function} props.onRetry - Retry function for error state
 * @param {Object} props.error - Error object if there's an error
 */
const ResourceList = ({ 
  resources = [], 
  loading = false, 
  searchTerm = '', 
  selectedCategories = [], 
  onRetry = null,
  error = null 
}) => {
  // Error state
  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <div className={styles.errorIcon}>
            <FileText size={48} />
          </div>
          <h3>Something went wrong</h3>
          <p>We couldn't load the resources. Please try again.</p>
          {onRetry && (
            <button 
              className={styles.retryButton}
              onClick={onRetry}
              type="button"
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingGrid}>
          {Array.from({ length: 6 }, (_, index) => (
            <ResourceCard key={index} loading={true} />
          ))}
        </div>
      </div>
    );
  }

  // Empty state
  if (!resources || resources.length === 0) {
    const hasFilters = searchTerm || selectedCategories.length > 0;
    
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            {hasFilters ? <Search size={48} /> : <FileText size={48} />}
          </div>
          <h3>
            {hasFilters ? 'No resources found' : 'No resources available'}
          </h3>
          <p>
            {hasFilters 
              ? 'Try adjusting your search terms or filters to find what you\'re looking for.'
              : 'Resources will appear here once they are added to the system.'
            }
          </p>
          {hasFilters && (
            <div className={styles.filterSummary}>
              {searchTerm && (
                <span className={styles.filterTag}>
                  Search: "{searchTerm}"
                </span>
              )}
              {selectedCategories.map((category, index) => (
                <span key={index} className={styles.filterTag}>
                  Category: {category}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Resources grid
  return (
    <div className={styles.container}>
      <div className={styles.resourceGrid}>
        {resources.map((resource) => (
          <ResourceCard 
            key={resource.id} 
            resource={resource} 
          />
        ))}
      </div>
      
      {/* Results summary */}
      <div className={styles.resultsSummary}>
        <p>
          Showing {resources.length} resource{resources.length !== 1 ? 's' : ''}
          {searchTerm && ` for "${searchTerm}"`}
          {selectedCategories.length > 0 && (
            <span>
              {' '}in {selectedCategories.length} categor{selectedCategories.length !== 1 ? 'ies' : 'y'}
            </span>
          )}
        </p>
      </div>
    </div>
  );
};

export default ResourceList;